import React, { useState, useEffect, useMemo, useRef } from "react";
import { Chip, InputAdornment, TextField } from "@mui/material";
import { Search } from "@mui/icons-material";

import { ModelBase, SubscriptionProps } from "@models";
import {
  Campaign,
  COptions,
  RuleDefinition,
  Subrace,
  Subclass,
  SKILLS,
  WEAPON_KEYWORDS,
  ARMOR_TYPES,
  SENSES,
  LANGUAGES,
} from "@dndModels";
import { DATA_TYPE_INFO } from "@dndModels/Constants";
import { DataType } from "@dndModels/Types";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from "@dndUtilities/data_utilities";
import FlexBox from "@components/layout/FlexBox";

import { BASIC_RULES, BASIC_RULE_CATEGORIES } from "./basic_rules";
import RulesReferenceEntry, { RefEntry } from "./RulesReferenceEntry";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

interface SectionDef {
  name: string;
  group: string;
  data_type?: DataType;
  constants?: ModelBase[];
}

const SECTIONS: SectionDef[] = [
  ...BASIC_RULE_CATEGORIES.map(c => ({ name: c, group: "Basic Rules" })),
  { name: "Rules Definitions", group: "Reference", data_type: "rule_definition" },
  { name: "Conditions", group: "Reference", data_type: "condition" },
  { name: "Skills", group: "Reference", constants: SKILLS as ModelBase[] },
  { name: "Weapon Properties", group: "Reference", constants: WEAPON_KEYWORDS as ModelBase[] },
  { name: "Weapon Mastery", group: "Reference", data_type: "weapon_mastery_property" },
  { name: "Armor Types", group: "Reference", constants: ARMOR_TYPES as ModelBase[] },
  { name: "Senses", group: "Reference", constants: SENSES as ModelBase[] },
  { name: "Languages", group: "Reference", constants: LANGUAGES as ModelBase[] },
  { name: "Races", group: "Character Options", data_type: "race" },
  { name: "Subraces", group: "Character Options", data_type: "subrace" },
  { name: "Classes", group: "Character Options", data_type: "game_class" },
  { name: "Subclasses", group: "Character Options", data_type: "subclass" },
  { name: "Backgrounds", group: "Character Options", data_type: "background" },
  { name: "Feats", group: "Character Options", data_type: "feat" },
  { name: "Fighting Styles", group: "Character Options", data_type: "fighting_style" },
  { name: "Eldritch Invocations", group: "Character Options", data_type: "eldritch_invocation" },
  { name: "Pact Boons", group: "Character Options", data_type: "pact_boon" },
];
const GROUPS = ["Basic Rules", "Reference", "Character Options"];
const LOADED_TYPES = SECTIONS.filter(s => s.data_type).map(s => s.data_type as DataType);
// Collapsed sections only show this many until expanded, so "All" stays quick to render.
const SECTION_CAP = 25;

// Same filtering the character builder applies (DataUtilities.getGood), but done
// locally so opening the rules doesn't swap the shared DataUtilities options out
// from under a character sheet that's open in the same encounter.
// Without campaign options (the standalone search page) everything the user can see is kept.
function filterForCampaign(objects: ModelBase[], data_type: DataType, c_options?: COptions): ModelBase[] {
  let filtered = objects.filter(o => !o.template_only);
  if (c_options) {
    filtered = filtered.filter(o =>
      common_util.anyOverlaps(o.source_book_ids, c_options.source_books) &&
      !c_options.blocked.includes(o._id) &&
      !c_options.blocked.includes(o.variant_group_id)
    );
    if (!c_options.world_settings.includes("Any")) {
      filtered = filtered.filter(o => o.world_settings.length === 0 || common_util.anyOverlaps(c_options.world_settings, o.world_settings));
    }
    const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
    if (dt_info && c_options.dominant_variants_only.includes(dt_info.dominance_group)) {
      filtered = [...data_util.getAllDominants(filtered), ...filtered.filter(o => o.isNonVersionVariant())];
    }
  }
  const seen = new Set<string>();
  return filtered.filter(o => !seen.has(o._id) && !!seen.add(o._id))
    .sort((a, b) => a.name.localeCompare(b.name));
}

interface Props {
  campaign?: Campaign;
}

export default function RulesReference({ campaign }: Props) {
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("All");
  const [expanded_sections, setExpandedSections] = useState<string[]>([]);
  // Keyed by campaign so switching campaigns doesn't show the last one's options.
  // "" is no campaign (the standalone search page).
  const campaign_id = campaign?._id ?? "";
  // Bumped whenever one of LOADED_TYPES finishes loading (or the user's source books
  // get worked out), so the sections get re-read. Data types load in stages - the
  // public scope first, then the user's own source books (homebrew, purchased books)
  // once those are known - so the first read can be missing things.
  const [load_tick, setLoadTick] = useState(0);
  const mounted = useRef(false);
  const [loaded_for, setLoadedFor] = useState<{ campaign_id: string | null, by_type: { [data_type: string]: ModelBase[] } }>({ campaign_id: null, by_type: {} });
  const loaded = useMemo(() =>
    loaded_for.campaign_id === campaign_id ? loaded_for.by_type : {},
  [loaded_for, campaign_id]);

  useEffect(() => {
    mounted.current = true;
    // subscribe() keeps the first method it's given for this ref, so these only
    // touch the stable state setter.
    api.subscription_holder.subscribe("Data Type Loaded", mounted, (loaded_event: SubscriptionProps) => {
      if (mounted.current && LOADED_TYPES.includes(loaded_event.value)) {
        setLoadTick(t => t + 1);
      }
    });
    api.subscription_holder.subscribe("My Things Loaded", mounted, () => {
      if (mounted.current) {
        setLoadTick(t => t + 1);
      }
    });
    // Start everything loading up front rather than one section at a time.
    LOADED_TYPES.forEach(data_type => {
      api.loadDataType(data_type, "priority").catch(ex => console.error(ex));
    });
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const c_options = campaign?.c_options;
    const store = (data_type: DataType, objects: ModelBase[]) => {
      if (active) {
        setLoadedFor(prev => ({
          campaign_id,
          by_type: { ...(prev.campaign_id === campaign_id ? prev.by_type : {}), [data_type]: objects },
        }));
      }
    };
    LOADED_TYPES.forEach(data_type => {
      api.getObjects<ModelBase>({ data_type, c_options }).then(objects => {
        store(data_type, filterForCampaign(objects, data_type, c_options));
      }).catch(ex => {
        console.error(ex);
        store(data_type, []);
      });
    });
    return () => { active = false; };
  }, [campaign, campaign_id, load_tick]);

  const entries_by_section = useMemo(() => {
    const races = loaded.race ?? [];
    const classes = loaded.game_class ?? [];
    const definitions = loaded.rule_definition ?? [];
    const by_section: { [section: string]: RefEntry[] } = {};
    SECTIONS.forEach(s => {
      if (s.group === "Basic Rules") {
        by_section[s.name] = BASIC_RULES.filter(r => r.category === s.name).map(rule => ({
          key: `rule_${rule.category}_${rule.name}`,
          section: s.name,
          name: rule.name,
          subtitle: "",
          keywords: rule.keywords ?? [],
          rule,
        }));
      } else {
        const objects = s.constants
          ? [...s.constants].sort((a, b) => a.name.localeCompare(b.name))
          : (loaded[s.data_type as string] ?? []);
        by_section[s.name] = objects.map(obj => ({
          key: `${s.name}_${obj._id}`,
          section: s.name,
          name: obj.name,
          subtitle: getSubtitle(obj, races, classes),
          keywords: [],
          obj,
          see_also: obj instanceof RuleDefinition
            ? definitions.filter(o => obj.see_also_ids.includes(o._id)).map(o => o.name)
            : undefined,
        }));
      }
    });
    return by_section;
  }, [loaded]);

  // Still pending until the user-scope part of the load is done too.
  const pending = LOADED_TYPES.filter(dt => !loaded[dt] || api.isDataTypeLoading(dt));
  const terms = search.toLowerCase().split(/\s+/).filter(t => t.length > 0);
  const visible_sections = SECTIONS.filter(s => section === "All" || section === s.name || section === s.group);

  const results = visible_sections.map(s => {
    const entries = entries_by_section[s.name] ?? [];
    if (terms.length === 0) {
      return { section: s, entries };
    }
    const scored = entries.map(e => ({ e, score: scoreEntry(e, terms) }))
      .filter(o => o.score > 0)
      .sort((a, b) => b.score - a.score || a.e.name.localeCompare(b.e.name));
    return { section: s, entries: scored.map(o => o.e) };
  }).filter(r => r.entries.length > 0);

  const renderSectionChips = () => (
    <FlexBox direction="column" style={{ gap: "4px" }}>
      <FlexBox direction="row" style={{ flexWrap: "wrap", gap: "4px" }}>
        <Chip label="All" size="small"
          color={section === "All" ? "primary" : "default"}
          onClick={() => setSection("All")} />
        { GROUPS.map(g => (
          <Chip key={g} label={g} size="small"
            color={section === g ? "primary" : "default"}
            onClick={() => setSection(g)} />
        ))}
      </FlexBox>
      { GROUPS.map(g => (
        <FlexBox key={g} direction="row" style={{ flexWrap: "wrap", gap: "4px" }}>
          { SECTIONS.filter(s => s.group === g).map(s => (
            <Chip key={s.name} label={s.name} size="small" variant="outlined"
              color={section === s.name ? "primary" : "default"}
              onClick={() => setSection(section === s.name ? g : s.name)} />
          ))}
        </FlexBox>
      ))}
    </FlexBox>
  );

  const renderResults = () => {
    if (results.length === 0) {
      return (
        <div className="gray" style={{ padding: "8px" }}>
          { pending.length > 0 ? "Loading..." : `Nothing found${ search ? ` for "${search}"` : "" }.` }
        </div>
      );
    }
    return results.map(({ section: s, entries }) => {
      const show_all = terms.length > 0 || section === s.name || expanded_sections.includes(s.name);
      const shown = show_all ? entries : entries.slice(0, SECTION_CAP);
      return (
        <FlexBox key={s.name} direction="column" style={{ marginBottom: "12px" }}>
          <div className="header">
            { s.name } <span className="small_label gray">({ entries.length })</span>
          </div>
          { shown.map(e => (
            <RulesReferenceEntry key={e.key} entry={e} auto_expand={entries.length === 1 && terms.length > 0} />
          ))}
          { shown.length < entries.length &&
            <div className="clickable small_label" style={{ padding: "4px 8px", textDecoration: "underline" }}
              onClick={() => setExpandedSections([...expanded_sections, s.name])}>
              Show all { entries.length } { s.name }
            </div>
          }
        </FlexBox>
      );
    });
  };

  return (
    <FlexBox direction="column" style={{ gap: "8px", padding: "4px" }}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        placeholder="Search rules, conditions, skills, races, classes, feats..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start"><Search /></InputAdornment>
            ),
          },
        }}
      />
      { renderSectionChips() }
      { pending.length > 0 &&
        <div className="small_label gray">
          Loading { pending.length } more section{ pending.length === 1 ? "" : "s" }...
        </div>
      }
      <FlexBox direction="column">
        { renderResults() }
      </FlexBox>
    </FlexBox>
  );
}

function getSubtitle(obj: ModelBase, races: ModelBase[], classes: ModelBase[]): string {
  const parts: string[] = [];
  if (obj instanceof Subclass) {
    const game_class = classes.find(o => o._id === obj.game_class_id);
    if (game_class) {
      parts.push(game_class.name);
    }
  } else if (obj instanceof Subrace) {
    const race_names = races.filter(o => obj.race_ids.includes(o._id)).map(o => o.name);
    parts.push(...race_names);
  }
  if (obj instanceof RuleDefinition && obj.rule_tag !== "None") {
    parts.push(obj.rule_tag);
  }
  const constant = obj as any;
  if (obj.data_type === "skill" && constant.use_ability_score && constant.use_ability_score !== "None") {
    parts.push(constant.use_ability_score);
  } else if (obj.data_type === "armor_type" && constant.stealth_disadvantage) {
    parts.push("Stealth Disadvantage");
  }
  if (obj.source_book_abbrev) {
    parts.push(obj.source_book_abbrev);
  }
  return parts.join(" · ");
}

// 0 means no match. Name matches rank above keyword/description matches.
function scoreEntry(entry: RefEntry, terms: string[]): number {
  const name = entry.name.toLowerCase();
  const description = entry.rule
    ? entry.rule.description
    : (entry.obj && entry.obj.loaded ? entry.obj.description : "");
  const other = `${entry.subtitle} ${entry.keywords.join(" ")} ${description ?? ""}`.toLowerCase();
  let score = 0;
  for (const term of terms) {
    if (name.startsWith(term)) {
      score += 4;
    } else if (name.includes(term)) {
      score += 3;
    } else if (other.includes(term)) {
      score += 1;
    } else {
      return 0;
    }
  }
  if (name === terms.join(" ")) {
    score += 10;
  }
  return score;
}
